import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { ContentItemInputBuilder } from 'components/WorkspaceEditor/factories/ContentItemInputBuilder'
import { SpecifierNotesInputBuilder } from 'components/WorkspaceEditor/factories/SpecifierNotesInputBuilder'
import {
    useContentItemViewNodes,
    useSelectedProjectElementNodeId,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'

/**
 * Service to handle content mutations
 * @returns {Object} - Object containing mutation functions
 */
export const useContentMutationService = () => {
    /** Mutation function to update the content item elements */
    const {
        updateProjectContentItemElement,
    } = useProjectContentItemElementsMutation()
    /** selected project element node id from global store */
    const selectedProjectElementNodeId = useSelectedProjectElementNodeId()
    /** the element selected on Element Tree */
    const {
        elementId: familyElementId,
        parentFamilyId,
        isProductTypeProject,
    } = useElementItemStore((s) => s.elementItemValue)
    /** project id from url */
    const { projectId } = useParams<{ projectId?: string }>()
    /** determine the element id based on project type */
    const elementId = (isProductTypeProject
        ? parentFamilyId
        : familyElementId) as string
    /** content item nodes from global store */
    const contentItemViewNodes = useContentItemViewNodes()

    /** Async mutation to update the content item element */
    const mutateContentElements = useCallback(
        async (
            contentItemId: string,
            changedElements: Partial<TextContentElement>[]
        ) => {
            // build input variables for mutation
            const input = new ContentItemInputBuilder({
                contentItemId,
                changedElements,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
            })

            const variables = input.build()

            return await updateProjectContentItemElement({
                variables,
            })
        },
        [
            updateProjectContentItemElement,
            elementId,
            selectedProjectElementNodeId,
            contentItemViewNodes,
            projectId,
        ]
    )

    /** Async mutation to update the content item element */
    const mutateNoteAsync = useCallback(
        async (
            contentItemId: string,
            specifierNotes: string[],
            isForArticle: boolean = false
        ) => {
            // build input variables for mutation
            const input = new SpecifierNotesInputBuilder({
                contentItemId,
                specifierNotes,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
                isForArticle,
            })

            const variables = input.build()

            return await updateProjectContentItemElement({
                variables,
            })
        },
        [
            updateProjectContentItemElement,
            elementId,
            selectedProjectElementNodeId,
            contentItemViewNodes,
            projectId,
        ]
    )

    return {
        mutateContentElements,
        mutateNoteAsync,
    }
}
